import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Typography, 
  Input, 
  Button, 
  Row, 
  Col, 
  Avatar, 
  Alert, 
  Modal, 
  Form,
  message,
  Divider,
  Space,
  Tag
} from 'antd';
import usePageTracking from '../utils/usePageTracking';
import { 
  UserOutlined, 
  MailOutlined, 
  PhoneOutlined, 
  EditOutlined,
  ShareAltOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  CopyOutlined
} from '@ant-design/icons';
import { useAuth } from './auth/AuthContext';
import { format } from 'date-fns';
import { API_CONFIG, apiClient } from '../api/config';
import { useAILimit } from './AIButtonLimitChecker';

import defaultAvatar from '../assets/avatar/default_avatar.png';
import avatarDe from '../assets/avatar/Avatar_de.png';
import oldman1 from '../assets/avatar/oldman1.jpg';
import man1 from '../assets/avatar/man1.jpg';
import girl1 from '../assets/avatar/gitl1.jpg';
import girl2 from '../assets/avatar/girl2.jpg';
import boy1 from '../assets/avatar/boy1.jpg';

// 预设头像列表
const defaultAvatars = [
  defaultAvatar,
  avatarDe,
  oldman1,
  man1,
  girl1,
  girl2,
  boy1
];

const ProfilePage = () => {
  // GA4 页面追踪
  usePageTracking('個人資料');
  
  const { isAuthenticated, user, updateProfile, resendVerification } = useAuth();
  const { checkAILimit } = useAILimit();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [selectedAvatar, setSelectedAvatar] = useState('');
  const [avatarModalOpen, setAvatarModalOpen] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [aiUsage, setAiUsage] = useState({ count: null, limit: null, accessTier: null });

  useEffect(() => {
    if (user) {
      form.setFieldsValue({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        bio: user.bio || ''
      });
      setSelectedAvatar(user.avatar_url || defaultAvatars[0]);
    }
  }, [user, form]);

  // 取得 AI 使用次數
  useEffect(() => {
    let isMounted = true;
    const fetchAiUsage = async () => {
      try {
        const result = await checkAILimit('PROFILE_PAGE');
        if (!isMounted) return;
        if (result) {
          const count = result.currentCount ?? result.limitInfo?.currentCount ?? null;
          const limit = result.limit ?? result.limitInfo?.limit ?? null;
          const accessTier = result.accessTier ?? result.limitInfo?.accessTier ?? null;
          setAiUsage({ count, limit, accessTier });
        }
      } catch (e) {}
    };
    if (isAuthenticated && user?.user_id) {
      fetchAiUsage();
    } else {
      setAiUsage({ count: null, limit: null, accessTier: null });
    }
    return () => { isMounted = false; };
  }, [isAuthenticated, user?.user_id]);

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      
      const updateData = {
        ...values,
        avatar_url: selectedAvatar
      };
      
      await updateProfile(updateData);
      
      message.success('個人資料更新成功');
    } catch (err) {
      message.error('更新個人資料失敗: ' + (err.message || '未知錯誤'));
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarClick = () => {
    setAvatarModalOpen(true);
  };

  const handleAvatarSelect = (avatar) => {
    setSelectedAvatar(avatar);
    setAvatarModalOpen(false);
  };

  const handleResendVerification = async () => {
    try {
      setIsResending(true);
      
      if (!user || !user.user_id) {
        throw new Error('無法獲取用戶信息');
      }
      
      await resendVerification();
      message.success('驗證郵件已發送，請查收');
    } catch (error) {
      message.error(error.response?.data?.message || error.message || '發送失敗，請稍後再試');
    } finally {
      setIsResending(false);
    }
  };

  const copyReferralLink = () => {
    const referralLink = `${window.location.origin}/register?ref=${user.referral_code}`;
    navigator.clipboard.writeText(referralLink).then(() => {
      message.success('推薦連結已複製到剪貼簿');
    }).catch(() => {
      message.error('複製失敗，請手動複製');
    });
  };

  if (!isAuthenticated || !user) {
    return (
      <div style={{ padding: '24px', maxWidth: '800px', margin: '0 auto' }}>
        <Alert
          message="請先登錄以查看個人資料"
          type="warning"
          showIcon
        />
      </div>
    );
  }

  return (
    <div style={{ 
      padding: '24px',
      maxWidth: '1000px',
      margin: '0 auto',
      backgroundColor: '#f5f5f5',
      minHeight: '100vh'
    }}>
      <Card style={{ borderRadius: '8px', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)' }}>
        <Typography.Title level={2} style={{ marginBottom: '24px' }}>
          個人資料
        </Typography.Title>
        
        <Row gutter={[32, 32]}>
          {/* 左側：頭像和基本信息 */}
          <Col xs={24} md={10}>
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <Avatar
                size={120}
                src={selectedAvatar}
                style={{ cursor: 'pointer', marginBottom: '12px' }}
                onClick={handleAvatarClick}
              />
              <div>
                <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
                  點擊更換頭像
                </Typography.Text>
              </div>
            </div>

            <Divider orientation="left">帳戶信息</Divider>
            
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              {aiUsage.count !== null && aiUsage.limit !== null && (
                <div>
                  <Typography.Text strong>
                    <CheckCircleOutlined style={{ marginRight: '8px', color: '#52c41a' }} />
                    AI 分析查詢使用次數
                  </Typography.Text>
                  <div style={{ marginTop: '4px' }}>
                    <Tag color="blue" style={{ fontSize: '14px', padding: '4px 8px' }}>
                      {aiUsage.count} / {aiUsage.limit}
                    </Tag>
                    {aiUsage.accessTier && (
                      <Tag color="geekblue" style={{ marginLeft: '8px' }}>
                        方案：{aiUsage.accessTier}
                      </Tag>
                    )}
                  </div>
                </div>
              )}
              <div>
                <Typography.Text strong>
                  <MailOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                  電子信箱
                </Typography.Text>
                <div style={{ marginTop: '4px' }}>
                  <Typography.Text>{user.email}</Typography.Text>
                  {user.is_verified ? (
                    <Tag color="green" style={{ marginLeft: '8px' }}>
                      <CheckCircleOutlined /> 已驗證
                    </Tag>
                  ) : (
                    <div style={{ marginTop: '8px' }}>
                      <Tag color="orange">
                        <ExclamationCircleOutlined /> 未驗證
                      </Tag>
                      <Button
                        type="link"
                        size="small"
                        onClick={handleResendVerification}
                        loading={isResending}
                        style={{ padding: '0 8px' }}
                      >
                        重新發送驗證信
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <Typography.Text strong>
                  <ShareAltOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                  推薦優惠碼
                </Typography.Text>
                <div style={{ marginTop: '4px' }}>
                  <Tag color="blue" style={{ fontSize: '14px', padding: '4px 8px' }}>
                    {user.referral_code}
                  </Tag>
                </div>
              </div>

              <div>
                <Typography.Text strong>推薦註冊連結</Typography.Text>
                <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center' }}>
                  <Input
                    value={`${window.location.origin}/register?ref=${user.referral_code}`}
                    readOnly
                    style={{ flex: 1, marginRight: '8px' }}
                  />
                  <Button
                    icon={<CopyOutlined />}
                    onClick={copyReferralLink}
                  >
                    複製
                  </Button>
                </div>
              </div>

              <div>
                <Typography.Text type="secondary">
                  註冊日期: {format(new Date(user.created_at), 'yyyy-MM-dd')}
                </Typography.Text>
              </div>
            </Space>
          </Col>
          
          {/* 右側：表單 */}
          <Col xs={24} md={14}>
            <Divider orientation="left">編輯資料</Divider>
            
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
              size="large"
            >
              <Form.Item
                label="姓名"
                name="name"
                rules={[{ required: true, message: '請輸入姓名' }]}
              >
                <Input 
                  prefix={<UserOutlined />}
                  placeholder="請輸入您的姓名"
                  disabled={loading}
                />
              </Form.Item>

              <Form.Item
                label="電子信箱"
                name="email"
              >
                <Input 
                  prefix={<MailOutlined />}
                  disabled
                  style={{ color: '#666' }}
                />
              </Form.Item>

              <Form.Item
                label="電話"
                name="phone"
              >
                <Input 
                  prefix={<PhoneOutlined />}
                  placeholder="請輸入您的電話號碼"
                  disabled={loading}
                />
              </Form.Item>

              <Form.Item
                label="個人簡介"
                name="bio"
              >
                <Input.TextArea 
                  rows={4}
                  placeholder="請輸入個人簡介"
                  disabled={loading}
                />
              </Form.Item>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  icon={<EditOutlined />}
                  size="large"
                >
                  保存修改
                </Button>
              </Form.Item>
            </Form>
          </Col>
        </Row>
      </Card>
      
      {/* 頭像選擇 Modal */}
      <Modal
        title="選擇頭像"
        open={avatarModalOpen}
        onCancel={() => setAvatarModalOpen(false)}
        footer={null}
        width={500}
      >
        <Row gutter={[16, 16]} style={{ padding: '16px 0' }}>
          {defaultAvatars.map((avatar, index) => (
            <Col span={6} key={index}>
              <div style={{ textAlign: 'center' }}>
                <Avatar
                  size={80}
                  src={avatar}
                  style={{
                    cursor: 'pointer',
                    border: selectedAvatar === avatar ? '3px solid #1890ff' : '2px solid #f0f0f0',
                    transition: 'all 0.3s ease'
                  }}
                  onClick={() => handleAvatarSelect(avatar)}
                />
              </div>
            </Col>
          ))}
        </Row>
      </Modal>
    </div>
  );
};

export default ProfilePage; 


